"use client"

import React from "react"
import { useSearchParams } from "react-router-dom"
import mainContext from "../../contexts/mainContext"
import { getHouseKeepersListing } from "../../Controllers/userManagment/houseKeeper"
import { deleteUser } from "../../Controllers/userManagment/common"

import UserCard from "./UserCard"
import Pagination from "@mui/material/Pagination"
import SearhcBar from "./SearhcBar"
import LoopIcon from "@mui/icons-material/Loop"
import { __constants, __snackBarText, __snackBarSeverity } from "../../constants"

import { exportToCSV, formatWithTZ } from "../../utils"
import moment from "moment"

const HouseKeepersListing = () => {
  const { setSnackBar, setProfileNavTitle, nth, months, loginUserDetails, setPageload, pageload } =
    React.useContext(mainContext)
  const [totalCount, setTotalCount] = React.useState(1)
  const [houseKeepers, setHouseKeepers] = React.useState([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [filterState, setFilterState] = React.useState({})
  const [searchParams] = useSearchParams()
  const autoOpenUserId = searchParams.get("userId")

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type],
    })
  }
  const getHouseKeepers = async (page, filterApply = true) => {
    let param = null
    if (filterApply) {
      param = { page: page, ...filterState }
      if (loginUserDetails.role === "Concierge") param.conceirgeId = loginUserDetails._id
    } else {
      param = { page: page }
      if (loginUserDetails.role === "Concierge") param.conceirgeId = loginUserDetails._id
    }
    setPageload(true)
    setHouseKeepers([])
    const res = await getHouseKeepersListing(param)
    setPageload(false)
    if (res && res.status === 500) {
      return callSnackBar(__snackBarText.internalServerError, "error")
    }
    if (res.data.status !== 200) {
      return callSnackBar(res.data.errors[0].message, "error")
    }
    setHouseKeepers(res.data.data.housekeeper)
    setTotalCount(res.data.data.total)
  }

  const handleExport = async () => {
    setPageload(true);
    let param = { ...filterState, page: 1, limit: 100000 };
    if (loginUserDetails.role === "Concierge") param.conceirgeId = loginUserDetails._id;
    const res = await getHouseKeepersListing(param);
    setPageload(false);
    if (res && res.data && res.data.data && res.data.data.housekeeper) {
      const formattedData = res.data.data.housekeeper.map(user => ({
        "First Name": user.firstName || "",
        "Last Name": user.lastName || "",
        "Email": user.email || "",
        "Mobile": user.mobileNo ? `${user.dialCode ? `${user.dialCode} ` : ""}${user.mobileNo}` : "",
        "City": user.city || "",
        "Date of Birth": user.dateOfBirth ? moment(user.dateOfBirth).format("YYYY-MM-DD") : "",
        "Added On": user.createdAt ? formatWithTZ(user.createdAt, "YYYY-MM-DD") : "",
        "Status": user.status || "Sleeping",
      }));
      exportToCSV(formattedData, `HouseKeepers_${moment().format("YYYYMMDD_HHmmss")}.csv`);
    } else {
      callSnackBar("Failed to export data", "error");
    }
  }

  const handlePaginationClick = (e, page) => {
    setCurrentPage(page)
    getHouseKeepers(page)
  }

  React.useEffect(() => {
    getHouseKeepers(1)
    setProfileNavTitle("HOUSEKEEPERS")
    // eslint-disable-next-line
  }, [])

  /* Handle update profile */
  const handleUpdate = (data, index) => {
    const temp = [...houseKeepers]
    temp[index] = data
    setHouseKeepers(temp)
  }

  /* Handle delete user */
  const handleDelete = async (userId) => {
    setPageload(true)
    const res = await deleteUser(userId)
    setPageload(false)

    if (res && res.status === 500) {
      return callSnackBar(__snackBarText.internalServerError, "error")
    }
    if (res.data.status !== 200) {
      return callSnackBar(res.data.errors[0].message, "error")
    }

    callSnackBar("User deleted successfully", "success")
    // Remove the deleted user from the list
    setHouseKeepers((prev) => prev.filter((hk) => hk._id !== userId))
    // Update total count
    setTotalCount((prev) => prev - 1)
  }

  return (
    <>
      <SearhcBar
        filterState={filterState}
        setFilterState={setFilterState}
        callFunction={getHouseKeepers}
        userType="housekeeper"
        onExport={handleExport}
      />
      <div className="component_body scrollbar">
        <div className="list_header Users_list_grid">
          <div>
            <div>Status</div>
          </div>
          <div>Username</div>
          <div>Email</div>
          <div>Phone</div>
          <div>City</div>
          <div>Onboarded</div>
          <div style={{ textAlign: "right" }}>Registered</div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <LoopIcon
              onClick={() => {
                getHouseKeepers(currentPage)
              }}
              className="pointer"
            />
          </div>
        </div>

        <div>
          {houseKeepers.map((e, index) => {
            const createdAt = new Date(e.createdAt)
            return (
              <UserCard
                key={e._id}
                userId={e._id}
                openCanvasOf="house-keeper"
                autoOpen={autoOpenUserId === e._id}
                status={e.status}
                city={e.city || "---"}
                name={e.firstName || e.lastName ? `${e.firstName || ""} ${e.lastName || ""}`.trim() : "---"}
                email={e.email || "---"}
                mobile={e.mobileNo ? `${e.dialCode ? `${e.dialCode} ` : ""}${e.mobileNo}` : "---"}
                userInfo={e}
                type="provider"
                stripe={e?.stripeAccountOnBoard?.completedOn ? true : false}
                paypal={e?.paypalAccountOnBoard?.completedOn ? true : false}
                onUpdate={(data) => handleUpdate(data, index)}
                onDelete={handleDelete}
                added={`${createdAt.getDate()}${nth(createdAt.getDate())} ${months[createdAt.getMonth()].slice(0, 3)} ${createdAt.getFullYear()}`}
              />
            )
          })}
        </div>
        {houseKeepers.length === 0 && !pageload && (
          <div className="no-record">
            <span>No Record Found</span>
          </div>
        )}
      </div>

      <Pagination
        count={Math.ceil(totalCount / __constants.listingLimit)}
        color="primary"
        shape="rounded"
        onChange={(e, page) => {
          handlePaginationClick(e, page)
        }}
      />
    </>
  )
}

export default HouseKeepersListing
